import { duration } from '../../utils/duration';
import { getJSTDaysDiff } from '../../utils/time';
import { magicLampsGetOngoingEvent } from '../../match2/magiclamps/magiclamps.getters';
import { magicLampsSchedule } from '../../match2/magiclamps/magiclamps.ruleset';
import {
  getActiveRace,
  getRaceStartTime,
} from '../../match2/races/races.getters';
import { getStreakBattleRoyaleResetTimestamp } from '../../streakbattleroyale/streakbattleroyale.getters';
import { getOngoingTreasureExpressEvent } from '../../treasureExpress/treasureExpress.getters';
import { jankenSchedule } from '../../janken/janken.ruleset';
import { buddiesSchedule } from '../../buddies/buddies.ruleset';
import { gachaEventSchedule } from '../../gacha/gacha.ruleset';
import { championsLeagueDuration } from '../../championsLeague/championsLeague.ruleset';
import { match2MaxLevelChangedAt } from '../../match2/match2.ruleset';
import {
  getMatch2ActiveFrenzyId,
  getMatch2FrenzyEventConfig,
} from '../../match2/frenzy/match2-frenzy.getters';
import { dreamDriftSchedule } from '../../dreamDrift/dreamDrift.ruleset';
import type { PromoGroupConfig, PromoGroupID } from './promoGroup.types';

/** Number of standard popup slots shared by launch promo groups. */
export const launchPromoSlotsAvailable = 2;

/**
 * Registry of all promo group configurations.
 *
 * Note: feature availability and popup launching are handled by the frontend
 * config in `src/game/promo/promo.launch.ts`. This config only defines timing,
 * recurrence, slot allocation, and priority ordering. Declaration order defines
 * group priority within the shared and extra slot pools.
 */
export const promoGroupConfig: {
  [GroupID in PromoGroupID]: PromoGroupConfig<GroupID>;
} = {
  dailyFeatures: {
    cooldown: duration({ days: 1 }),
    maxSlots: 2,
    priorityMode: 'configured',
    items: [
      { id: 'gifts' },
      {
        id: 'friendInvite',
        cooldown: (state, now) => {
          // Player age uses the same JST day count as projectDaysSinceInstall.
          const daysSinceInstall = getJSTDaysDiff(now, state.createdAt);

          // Up to install day 7: every launch. The 1 minute only stops a second
          // popup in the same launch after Gifts handed off to the invite flow.
          if (daysSinceInstall <= 7) return duration({ minutes: 1 });

          // Older players: 6 hours since the last automatic popup.
          return duration({ hours: 6 });
        },
      },
    ],
  },
  recurringFeatures: {
    cooldown: duration({ days: 1 }),
    maxSlots: 1,
    priorityMode: 'configured',
    items: [
      {
        id: 'magicLamps',
        eventID: (_state, now) => {
          return magicLampsGetOngoingEvent(now)?.id;
        },
        eventStartAt: (_state, now) => {
          return magicLampsSchedule.getCurrentEvent(now)?.start;
        },
      },
      {
        id: 'streakQuest',
        eventID: (_state, now) => {
          return String(getStreakBattleRoyaleResetTimestamp(now));
        },
        eventStartAt: (_state, now) => {
          return (
            getStreakBattleRoyaleResetTimestamp(now) - duration({ days: 1 })
          );
        },
      },
      {
        id: 'inviteChallenge',
        eventID: (state) => {
          // Invite challenges are uniquely identified by their start time.
          const startTime = state.social.challenge.startTime;

          // A challenge with no start time is not active.
          if (startTime <= 0) return undefined;

          // Use the start timestamp as the stable challenge instance ID.
          return String(startTime);
        },
        eventStartAt: (state) => {
          return state.social.challenge.startTime > 0
            ? state.social.challenge.startTime
            : undefined;
        },
      },
      {
        id: 'jankenEventPromo',
        eventID: (_state, now) => {
          return jankenSchedule.getCurrentEvent(now)?.id;
        },
        eventStartAt: (_state, now) => {
          return jankenSchedule.getCurrentEvent(now)?.start;
        },
      },
      {
        id: 'gacha',
        eventID: (_state, now) => {
          return gachaEventSchedule.getCurrentEvent(now)?.id;
        },
        eventStartAt: (_state, now) => {
          return gachaEventSchedule.getCurrentEvent(now)?.start;
        },
      },
      {
        id: 'treasureExpress',
        eventID: (state, now) => {
          return getOngoingTreasureExpressEvent(state, now)?.id;
        },
        eventStartAt: (state, now) => {
          return getOngoingTreasureExpressEvent(state, now)?.start;
        },
      },
      {
        id: 'raceIntro',
        eventID: (_state, now) => {
          return getActiveRace(now)?.id;
        },
        eventStartAt: (_state, now) => {
          // Retrieve the active race once so the start lookup uses the same ID.
          const id = getActiveRace(now)?.id;

          // A missing race has no event start time.
          if (id === undefined) return undefined;

          // Return the start timestamp for the active race instance.
          return getRaceStartTime(id);
        },
      },
      {
        id: 'championsLeague',
        eventID: (_state, now) => {
          // The current Champions League cycle starts with the latest level release.
          const startTime = match2MaxLevelChangedAt.getTime();

          // The cycle is only active during its configured two-week duration.
          if (now < startTime || now >= startTime + championsLeagueDuration) {
            return undefined;
          }

          // Use the level-release timestamp as the stable cycle ID.
          return String(startTime);
        },
        eventStartAt: () => {
          return match2MaxLevelChangedAt.getTime();
        },
      },
      {
        id: 'frenzy',
        eventID: (state, now) => {
          return getMatch2ActiveFrenzyId(state, now) ?? undefined;
        },
        eventStartAt: (state, now) => {
          return getMatch2FrenzyEventConfig(state, now)?.start.getTime();
        },
      },
    ],
  },
  ftueFeatures: {
    cooldown: duration({ days: 1 }),
    extraSlots: 1,
    priorityMode: 'neverSeenFirst',
    randomizeUndatedTies: true,
    items: [
      {
        id: 'buddies',
        showOnce: true,
        eventID: (_state, now) => {
          return buddiesSchedule.getCurrentEvent(now)?.id;
        },
        eventStartAt: (_state, now) => {
          return buddiesSchedule.getCurrentEvent(now)?.start;
        },
      },
      {
        id: 'dreamDrift',
        eventID: (_state, now) => {
          return dreamDriftSchedule.getCurrentEvent(now)?.id;
        },
        eventStartAt: (_state, now) => {
          return dreamDriftSchedule.getCurrentEvent(now)?.start;
        },
      },
      { id: 'superLollipop', showOnce: true },
      { id: 'homeShortcut', showOnce: true },
      { id: 'cards', showOnce: true },
      {
        id: 'janken',
        showOnce: true,
        eventID: (_state, now) => {
          return jankenSchedule.getCurrentEvent(now)?.id;
        },
        eventStartAt: (_state, now) => {
          return jankenSchedule.getCurrentEvent(now)?.start;
        },
      },
      {
        id: 'treasureExpress',
        showOnce: true,
        eventID: (state, now) => {
          return getOngoingTreasureExpressEvent(state, now)?.id;
        },
        eventStartAt: (state, now) => {
          return getOngoingTreasureExpressEvent(state, now)?.start;
        },
      },
    ],
  },
};
